/**
 * The light show in the XLMOTO tent, as numbers.
 *
 * Same split as `Plume.ts` and for the same reason: `World` owns the meshes and
 * the point light, this owns *when* anything happens. Ask what strobe head `n`
 * is doing at render second `t` and you get the same answer on every machine,
 * however many frames it took to get there - so a rig that has been flashing
 * since the page opened cannot drift, cannot accumulate and cannot be got out
 * of step by a tab that went to the background for a minute.
 *
 * All of it is a pure function of wall-clock render time. Nothing here is
 * stepped, nothing here is stored, and nothing here is reachable from
 * `src/sim`: a run that is reset does not rewind the party, because nobody in
 * the tent is watching the run.
 */

import { DISCO } from '../config/gameplay';
import { clamp } from '../utils/math';
import type { LaserSpec, StrobeSpec } from '../world/Level';

/**
 * How hard one head is firing right now, 0..1.
 *
 * A strobe is a pair of hits and then nothing: `flash` seconds on, dark, then
 * the same hit again `doubleGap` of a period later, then the rest of the cycle
 * with the lamp cold. That "then nothing" is most of the period and it is the
 * part that makes it a strobe - a lamp that is on half the time is a lamp.
 *
 * Each head gets its own slice of the same cycle through `phase`, so the four
 * of them chase each other round the tent instead of blinking as one square of
 * light. The falling edge is a short ramp rather than a cliff, because a hit
 * that is over inside one frame at 60 Hz is a hit half the machines in the room
 * would miss entirely - the ramp is what guarantees at least one frame with
 * something on it.
 */
export function strobeFlash(spec: StrobeSpec, renderTime: number): number {
  const period = DISCO.period;
  // Wrapped into 0..period from the head's own offset. `%` on a negative is
  // negative in JS, and a phase past 1 is a legal way to say "a cycle later",
  // so this is normalised rather than trusted.
  const at = (((renderTime - spec.phase * period) % period) + period) % period;
  return Math.max(hit(at), hit(at - DISCO.doubleGap * period));
}

/**
 * One hit, `age` seconds after it started. Full for the first half of `flash`
 * and ramped off over the second half, so a slow display still catches it on
 * the way down.
 */
function hit(age: number): number {
  if (age < 0 || age >= DISCO.flash) return 0;
  const half = DISCO.flash / 2;
  return age <= half ? 1 : 1 - (age - half) / half;
}

/** The brightest head in the rig this frame - what the shared point light follows. */
export function rigFlash(strobes: readonly StrobeSpec[], renderTime: number): number {
  let brightest = 0;
  for (const strobe of strobes) brightest = Math.max(brightest, strobeFlash(strobe, renderTime));
  return brightest;
}

/**
 * How lit the mirror ball is, 0..1.
 *
 * The rig's own flash with a floor under it, because a ball that is only lit on
 * the frames a strobe fires is a ball that is not there for seven eighths of
 * the time. `DISCO.ballGlow` is the part of the party that hangs on it between
 * hits - which, on a ball hung two feet from a laptop screen and a pair of
 * lasers, is exactly what happens.
 */
export function ballGlint(strobes: readonly StrobeSpec[], renderTime: number): number {
  return clamp(DISCO.ballGlow + (1 - DISCO.ballGlow) * rigFlash(strobes, renderTime), 0, 1);
}

/** How far round the mirror ball has got, in radians. */
export function ballAngle(renderTime: number): number {
  return (renderTime / DISCO.ballSpin) * Math.PI * 2;
}

/**
 * How far one laser projector is swung off the middle of its fan, in radians.
 *
 * A triangle wave rather than a sine, and that is the whole character of the
 * thing: a scanner drives its mirror at a constant rate and turns round at the
 * ends, so the beams cross the yard evenly and snap back. A sine spends most of
 * its time near the ends of the sweep, which reads as a light that is hesitating.
 */
export function laserSwing(spec: LaserSpec, renderTime: number): number {
  const phase = (((renderTime / DISCO.laserSweep + spec.phase) % 1) + 1) % 1;
  // 0 -> 1 -> 0 across the cycle, then mapped onto -swing .. +swing.
  const triangle = phase < 0.5 ? phase * 2 : 2 - phase * 2;
  return (triangle * 2 - 1) * DISCO.laserSwing;
}
